import React from 'react';
import SEO from '../components/layout/SEO';
import { Shield, Lock, Eye, Database } from 'lucide-react';

const Privacy: React.FC = () => {
  const sections = [
    {
      icon: Shield,
      title: 'Data Protection',
      content: 'We implement industry-standard security measures to protect your personal information. All data is encrypted in transit and at rest using SSL/TLS protocols.'
    },
    {
      icon: Lock,
      title: 'Information Collection',
      content: 'We collect information you provide directly to us, such as when you create an account, contact us, or use our services. This may include name, email, and usage data.'
    },
    {
      icon: Eye,
      title: 'How We Use Information',
      content: 'We use the information we collect to provide, maintain, and improve our services, communicate with you, and ensure the security of our platform.'
    },
    {
      icon: Database,
      title: 'Data Storage',
      content: 'Your data is stored securely on servers located in the United States. We do not sell, trade, or otherwise transfer your personal information to third parties.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Privacy Policy - tadriso LMS"
        description="Learn about how tadriso LMS protects your privacy and handles your personal information."
        keywords="privacy policy, data protection, GDPR, user privacy, tadriso LMS"
      />
      
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Privacy
              <span className="text-primary-600 block">Policy</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <section.icon size={24} className="text-primary-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Information */}
          <div className="mt-16 bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Your Rights
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                <p className="text-gray-600">
                  <strong>Access:</strong> You have the right to access the personal information we hold about you.
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                <p className="text-gray-600">
                  <strong>Correction:</strong> You can request corrections to your personal information.
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                <p className="text-gray-600">
                  <strong>Deletion:</strong> You can request deletion of your personal information.
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                <p className="text-gray-600">
                  <strong>Portability:</strong> You can request a copy of your data in a portable format.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-16 bg-primary-50 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Contact Us
            </h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="space-y-2">
              <p className="text-gray-600">
                <strong>Email:</strong> privacy@ciphermind.tech
              </p>
              <p className="text-gray-600">
                <strong>Address:</strong> Lahore, Pakistan
              </p>
              <p className="text-gray-600">
                <strong>Phone:</strong> +92 (308) 7700430
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy; 